import { Link, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAccount, useChainId, useSignTypedData, useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi';
import BigNumber from 'bignumber.js';
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useCookies } from 'react-cookie';

import bnbLogo from '../images/bnb-bnb-logo.svg';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SwitchNetwork } from '../components/SwitchNetwork';

import { NETWORK, ADDRESSES, ABIS, APIURL } from "../config";

const isEmail = (email) =>
  /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email);

const LOGIN_MESSAGE = 'Log in to app.biofitoken.io';

const domain = {
  name: 'biofieportal',
  chainId: NETWORK.id,
  verifyingContract: '0x11a31b833d43853f8869c9eec17f60e3b4d2a753',
  version: '1',
};

const types = {
  Message: [
    { name: 'message', type: 'string' },
    { name: 'sessionUser', type: 'address' },
    { name: 'sessionExpiringAt', type: 'uint256' },
  ],
};

console.log("domain", domain);

export default function Login({ validChain }) {
  const [emailAddress, setEmailAddress] = useState('');
  const [emailValid, setEmailValid] = useState(false);
  const [userAlreadyRegistered, setUserAlreadyRegistered] = useState(null);

  const [cookies, setCookie] = useCookies();
  const [signMessage, setSignMessage] = useState({ message: "", sessionUser: "", sessionExpiringAt: "" });
  const [signedMessage, setSignedMessage] = useState("");
  const [updateRequired, setUpdateRequired] = useState(true);
  const [userBiofiBalance, setUserBiofiBalance] = useState(0);
  const [isStaker, setIsStaker] = useState(false);



  const account = useAccount();
  const address = account.address;
  let isWalletConnected = !!address;

  const chainId = useChainId();
  let isNetworkCorrect = chainId === validChain?.id;

  const signMsgLogin = useSignTypedData();

  useEffect(() => {
    if (signMsgLogin.isError) {
      console.error(signMsgLogin.error);
      toast.error("Signature request rejected");
    } else if (signMsgLogin.isSuccess) {
      console.log(signMsgLogin.data);
      setSignedMessage(signMsgLogin.data);
    }
  }, [signMsgLogin.status]);


  const { data: userBalanceData, refetch: refetchUserBalance } = useReadContract({
    address: ADDRESSES[NETWORK.id].biofi,
    abi: ABIS.biofi,
    functionName: "balanceOf",
    args: [address],
    enabled: !!address,
    watch: true,
  });

  const { data: userStakeData, refetch: refetchUserStake } = useReadContract({
    address: ADDRESSES[NETWORK.id].staking,
    abi: ABIS.staking,
    functionName: "readStake",
    args: [address],
    enabled: !!address,
    watch: false,
  });

  const { writeContractAsync: claimTokensWrite, data: claimTxData } = useWriteContract();

  const waitForClaim = useWaitForTransactionReceipt({
    hash: claimTxData,
  });

  useEffect(() => {
    if (!waitForClaim.isPending) {
      if (waitForClaim.isSuccess) {
        toast.success("$BioFi claimed successfully");
        console.log('$BioFi claimed successfully', waitForClaim.data);
        fetchUserBalance();
      } else if (waitForClaim.isError) {
        console.log('Error', waitForClaim.error)
        toast.error("Claiming tokens failed - try again");
      }
    } else if (waitForClaim.data) {
      toast("Transaction pending...");
    }
  }, [waitForClaim.status]);

  async function claimBioFi() {
    toast("Confirm the transaction in your wallet");
    try {
      await claimTokensWrite({
        address: ADDRESSES[NETWORK.id].faucet,
        abi: ABIS.faucet,
        functionName: "claim",
      });
    } catch (e) {
      console.error(e);
    }
  }

  async function fetchUserBalance() {
    if (userBalanceData === undefined) return;
    let userBalance = BigNumber(userBalanceData.toString()).dividedBy(10 ** 18);
    setUserBiofiBalance(userBalance.toNumber());
  }

  async function fetchUserStake() {
    if (userStakeData === undefined) return;
    let stakeExists = userStakeData.exists;
    setIsStaker(stakeExists);
  }

  useEffect(() => {
    fetchUserBalance();
  }, [userBalanceData]);

  useEffect(() => {
    fetchUserStake();
  }, [userStakeData]);

  function emailChange(event) {
    let email = event.target.value;
    let isEmailValid = (email.length > 0) && isEmail(email);
    setEmailAddress(email);
    setEmailValid(isEmailValid);
  }

  async function submitNewUser() {
    console.log("submitNewUser");
    if (address === undefined) return;
    if (cookies.sessionExpiringAt === undefined || cookies.sessionExpiringAt === "") return;
    if (cookies.sessionSignature === undefined || cookies.sessionSignature === "") return;

    let userType = 'user';
    if (cookies.userType === undefined || cookies.userType === "") {
      userType = 'user';
    } else {
      userType = cookies.userType;
    }

    console.log('registering new user: ' + emailAddress + ', ' + address);

    let session = address + ';' + cookies.sessionExpiringAt + ';' + cookies.sessionSignature;

    let requestUrl = APIURL + '/biofiportal/register?EmailAddress=' + encodeURIComponent(emailAddress) + '&WalletAddress=' + address + '&UserType=' + userType + '&SessionData=' + session;

    const response = await fetch(requestUrl, {
      method: 'POST',
      mode: 'cors',
      redirect: 'follow',
      referrerPolicy: 'no-referrer'
    });

    if (!response.ok) {
      toast.error('Registration failed. Try again later');
      console.error("[submitNewUser] " + response.status + " code detected. Request failed.");
      return;
    }

    const jsonResponse = await response.json();

    toast.success('Registration successful');
    setUserAlreadyRegistered(true);
    console.log(jsonResponse.msg);
  }

  function logout() {
    console.log("logout");

    let expiresAt = new Date(0);
    setCookie('sessionUser', '', { path: '/', expires: expiresAt });
    setCookie('sessionExpiringAt', '', { path: '/', expires: expiresAt });
    setCookie('sessionSignature', '', { path: '/', expires: expiresAt });
    setCookie('sessionUserType', '', { path: '/', expires: expiresAt });

  }

  async function checkUserStatus() {
    if (address === undefined) return;
    if (cookies.sessionExpiringAt === undefined || cookies.sessionExpiringAt === "") return;
    if (cookies.sessionSignature === undefined || cookies.sessionSignature === "") return;

    let session = address + ';' + cookies.sessionExpiringAt + ';' + cookies.sessionSignature;

    try {
      let requestUrl = APIURL + '/biofiportal/fetchUserData?WalletAddress=' + address + '&SessionData=' + session;

      const response = await fetch(requestUrl, {
        method: 'GET',
        mode: 'cors',
        redirect: 'follow',
        referrerPolicy: 'no-referrer'
      });

      if (!response.ok) {
        console.error("[checkUserStatus] " + response.status + " code detected. Request failed.");
        if (response.status === 401) {
          console.log("session data mismatch - forcing logout...");
          logout();
        }
        return;
      }

      const jsonResponse = await response.json();

      console.log("isRegistered", jsonResponse.isRegistered);
      if (jsonResponse.isRegistered) {
        setUserAlreadyRegistered(true);
        setCookie('userType', jsonResponse.userType, { path: '/' });
        setCookie('sessionUserType', jsonResponse.userType, { path: '/' });
      } else {
        setUserAlreadyRegistered(false);
      }
    } catch (e) {
      console.error(e);
    }

  }

  useEffect(() => {
    checkUserStatus();
  }, [cookies]);

  async function confirmLogin() {
    console.log("confirmLogin");

    if (address === undefined) return;

    let url = APIURL + "/biofiportal/biofi/staking/confirmLogin?userAddress=" + address;

    let response = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      redirect: 'follow',
      referrerPolicy: 'no-referrer'
    });

    if (response.ok) {
      let jsonRes = await response.json();
      console.log('Request succeeded with JSON response', jsonRes);
    } else {
      console.error("Error confirming login. Try again later");
    }
  }


  function sessionValid() {
    let isSessionValid = (!updateRequired && cookies.sessionUser !== undefined && cookies.sessionSignature !== undefined && cookies.sessionExpiringAt !== undefined && cookies.sessionExpiringAt > Date.now());
    if (isSessionValid) {
      confirmLogin();
    }
    return isSessionValid;
  }

  function signSessionMessage(forced) {
    console.log("signSessionMessage");
    console.log("signMessage", signMessage);
    console.log("address", address);
    if (signMessage.message === "" || signMessage.sessionUser === "" || signMessage.sessionExpiringAt === "") return;
    if (signMessage.sessionUser !== address) return;
    console.log("signSessionMessage ok");

    signMsgLogin.signTypedData({
      domain,
      types,
      primaryType: 'Message',
      message: {
        message: signMessage.message,
        sessionUser: signMessage.sessionUser,
        sessionExpiringAt: signMessage.sessionExpiringAt,
      },
    });

  }

  function signSessionMessageManual() {
    signSessionMessage(true);
  }

  useEffect(() => {
    signSessionMessage(false);
  }, [signMessage]);

  function inputDataValid(input) {
    return input !== undefined && input !== "" && input !== "undefined" && input !== null;
  }

  useEffect(() => {
    console.log("useEffect[signedMessage]");
    if (!inputDataValid(address) || !inputDataValid(signMessage?.sessionExpiringAt) || !inputDataValid(signedMessage)) return;

    let expiresAt = new Date(Date.now() + (24 * 60 * 60 * 1000));

    setCookie('sessionUser', address, { path: '/', expires: expiresAt });
    setCookie('sessionExpiringAt', signMessage.sessionExpiringAt, { path: '/', expires: expiresAt });
    setCookie('sessionSignature', signedMessage, { path: '/', expires: expiresAt });
    setCookie('sessionUserType', cookies.userType, { path: '/', expires: expiresAt });

    console.log("Message signed successfully", signedMessage);
    toast.success("Logged in successfully!");

    setUpdateRequired(false);

  }, [signedMessage]);

  useEffect(() => {
    console.log("useEffect[address]");
    if (address === "") return;
    if (cookies.sessionUser === undefined || cookies.sessionSignature === undefined || cookies.sessionExpiringAt === undefined || cookies.sessionExpiringAt <= Date.now()) {
      console.log("update required");
      setUpdateRequired(true);

      let endTimestamp = Date.now() + (24 * 60 * 60 * 1000);

      let newSignMessage = { message: LOGIN_MESSAGE, sessionUser: address, sessionExpiringAt: endTimestamp };
      setSignMessage(newSignMessage);
    } else {
      setUpdateRequired(false);
    }

  }, [address]);


  const useWriteClaimTokens = useWriteContract({
    address: ADDRESSES[NETWORK.id].faucet,
    abi: ABIS.faucet,
    functionName: "claim",
    onError(error) {
      console.log('Error', error)
      toast.error("Claiming tokens failed - try again");
    },
    onSuccess(data) {
      console.log('claim tx submitted', data);
      toast("Transaction pending...");
      const txResult = waitForClaim;
      console.log(txResult);
    },
  });

  function claimBioFi() {
    toast("Confirm the transaction in your wallet");
    try {
      useWriteClaimTokens.write();
    } catch (e) {
      console.error(e);
    }
  }

  return (
    <>
      <div className="page-wrapper">

        <Navbar validChain={validChain} />

        <section className="bf-main">
          <div className="main-content">
            <main>
              <div id="section1" className="container">
                <div className="bf-container2">
                  {
                    isWalletConnected ? (
                      isNetworkCorrect ? (
                        userAlreadyRegistered === false ? (
                          userBiofiBalance >= 1 || isStaker ? (
                            <>
                              <div className="bf-auth-hdr">
                                <h1>Welcome to the BioFi Utility Ecosystem</h1>
                              </div>
                              <div className="bf-newUsrLgn">
                                <h2>New User:</h2>
                                <hr width={200} />
                                <span>We've noticed this is your first time signing in. Please provide your email address below, to complete your account registration.</span>
                                <strong>Your email address:</strong>
                                <input type="email" className="bf-newUsrEmail" value={emailAddress} onChange={emailChange} />
                                <button disabled={!emailValid || userAlreadyRegistered} className="bf-newUsrBtn" onClick={submitNewUser}>{userAlreadyRegistered ? 'Already registered' : 'Register'}</button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="bf-auth-hdr">
                                <h1>Welcome to the BioFi Utility Ecosystem</h1>
                              </div>
                              <div className="bf-newUsrLgn">
                                <span>Unfortunately, you don't have enough $BioFi in your wallet to complete the account registration. Make sure you hold at least 1 $BioFi to access the ecosystem.</span>
                                <strong>Your current balance: {userBiofiBalance}</strong>
                                <br /><br />
                                <h3>BioFi Faucet</h3>
                                <span>You can click the button below to request 1 free $BioFi. You'll be asked to sign a transaction in your wallet and cover the gas fees. Each user can only claim 1 BioFi from this faucet.</span>
                                <button className="bf-newUsrBtn" onClick={claimBioFi}>Claim 1 $BioFi</button>
                              </div>
                            </>
                          )
                        ) : (
                          userAlreadyRegistered && sessionValid() ? (
                            <Navigate replace to="/biofiRewards" />
                          ) : (
                            <>
                              <div className="bf-newUsrLgn">
                                <h2>Sign message to proceed</h2>
                                <hr />
                                <span>Thank you for being a part of the BioFi Ecosystem. Your digital signature is required to proceed to login.</span>
                                <button onClick={signSessionMessageManual} className="bf-newUsrBtn" >Sign</button>
                              </div>
                            </>
                          )
                        )
                      ) : (
                        <>
                          <div className="bf-auth-hdr">
                            <h1>Biometric Financial Utility Ecosystem</h1>
                          </div>
                          <div className="bf-sect1-auth-window">
                            <SwitchNetwork validChain={validChain} />
                          </div>
                        </>
                      )
                    ) : (
                      <>
                        <div className="bf-auth-hdr">
                          <h1>Biometric Financial Utility Ecosystem</h1>
                        </div>
                        <div className="bf-sect1-auth-window">
                          <div className="bf-sect1-sub1">
                            <img src={bnbLogo} width={30} height={30} alt="" className="bf-avax-logo" />
                            <div className="bf-sect1-txt2"> Your BSC Wallet Address</div>
                            <img src={bnbLogo} width={30} height={30} alt="" className="bf-avax-logo" />
                          </div>
                          <p className="bf-p-txt">
                            If you don't see your BSC Wallet address above, please click the 'Connect Wallet' button below. For further help, visit our <a href="https://info.biometricfinancial.org/faq/" target="_blank" rel="noreferrer">FAQ page</a>
                          </p>
                          <br />
                          <ConnectButton showBalance={false} chainStatus="icon" />
                        </div>
                      </>
                    )
                  }

                </div>
              </div>
            </main>
          </div>
        </section>

        <Footer />
      </div>

    </>
  );
}